/* ==========================================================
   BR STUDIO — hero smoke loop
   Seamless 18s looping fume field. Rendered at a fixed LOW
   resolution and upscaled by the browser (bilinear upscale =
   free softness), so there is no per-frame CSS blur or blend.
   Throttled to ~28fps. Palette-locked and text-safe.
   Mount: <div class="hero-smoke" data-hero-smoke></div>
   ========================================================== */
(function () {
  const LOOP = 18;        // seconds per cycle
  const PLUMES = 16;
  const FPS = 28;
  const BUF_W = 240;      // fixed render buffer — upscaled by CSS
  const BUF_H = 135;
  // Muted palette — deep rust / deep teal dominate; light tones dimmed so the
  // smoke reads as atmosphere and never pops off the page.
  const PAL = [
    [92, 47, 27], [92, 47, 27], [92, 47, 27], [92, 47, 27],
    [32, 72, 68], [32, 72, 68], [32, 72, 68],
    [70, 108, 105],
    [120, 96, 82]
  ];

  const rnd = (s) => { const x = Math.sin(s * 127.1) * 43758.5453; return x - Math.floor(x); };

  function makePlumes() {
    const out = [];
    for (let i = 0; i < PLUMES; i++) {
      const a = rnd(i + 1), b = rnd(i + 9.3), c = rnd(i + 21.7), d = rnd(i + 44.1);
      out.push({
        x: 0.08 + a * 0.92,
        r: 0.22 + b * 0.42,
        phase: c,
        sway: 0.03 + d * 0.09,
        swayK: 1 + Math.floor(d * 3),
        rise: 0.55 + b * 0.65,
        col: PAL[Math.floor(rnd(i + 63.2) * PAL.length)],
        peak: 0.11 + c * 0.13
      });
    }
    return out;
  }

  /* Occluders: near-black volumes drifting in FRONT of the plumes. They carve
     the haze into layers — the single biggest contributor to a cinematic,
     volumetric read (and they darken rather than brighten the frame). */
  function makeOccluders() {
    const out = [];
    for (let i = 0; i < 7; i++) {
      const a = rnd(i + 301.5), b = rnd(i + 412.9), c = rnd(i + 517.3);
      out.push({
        x: 0.05 + a * 0.95,
        r: 0.34 + b * 0.5,
        phase: c,
        rise: 0.4 + a * 0.5,
        sway: 0.05 + c * 0.1,
        swayK: 1 + Math.floor(b * 3),
        peak: 0.32 + b * 0.3
      });
    }
    return out;
  }

  function mount(host) {
    const cv = document.createElement('canvas');
    cv.className = 'hero-smoke__cv';
    cv.width = BUF_W; cv.height = BUF_H;
    cv.setAttribute('aria-hidden', 'true');
    host.appendChild(cv);
    const ctx = cv.getContext('2d');
    const plumes = makePlumes();
    const occ = makeOccluders();
    const S = Math.min(BUF_W, BUF_H);
    let raf = 0, running = true, last = -1e9;

    /* ---- storm flash: rare, dim, off-screen-lightning feel ---- */
    const FLASH_MIN = 5.5, FLASH_MAX = 14;   // seconds between strikes
    let flashAt = 3 + Math.random() * FLASH_MAX, flash = null;
    function scheduleFlash(tSec) {
      flashAt = tSec + FLASH_MIN + Math.random() * (FLASH_MAX - FLASH_MIN);
      flash = {
        t0: tSec,
        dur: 0.45 + Math.random() * 0.5,
        x: 0.15 + Math.random() * 0.8,
        y: -0.1 + Math.random() * 0.7,
        r: 0.7 + Math.random() * 0.9,
        peak: 0.05 + Math.random() * 0.055,      // deliberately faint
        blinks: Math.random() < 0.45 ? 2 : 1
      };
    }
    // lightning envelope: fast attack, ragged decay, optional second blink
    function flashLevel(f, tSec) {
      const u = (tSec - f.t0) / f.dur;
      if (u < 0 || u > 1) return 0;
      let e = u < 0.08 ? u / 0.08 : Math.pow(1 - (u - 0.08) / 0.92, 2.2);
      if (f.blinks === 2) {
        const v = (u - 0.30) / 0.22;
        if (v > 0 && v < 1) e = Math.max(e, 0.72 * Math.pow(1 - v, 1.8));
      }
      return e * (0.75 + 0.25 * Math.sin(tSec * 90));   // slight flicker
    }

    function draw(tSec) {
      const t = (tSec % LOOP) / LOOP;
      ctx.clearRect(0, 0, BUF_W, BUF_H);
      ctx.globalCompositeOperation = 'lighter';
      for (const p of plumes) {
        const u = (t + p.phase) % 1;
        const fade = Math.sin(Math.PI * u);
        if (fade <= 0.002) continue;
        const R = p.r * S * (0.55 + u * 0.85);
        const x = (p.x + p.sway * Math.sin(2 * Math.PI * (p.swayK * (t + p.phase)))) * BUF_W;
        const y = BUF_H * (1.12 - u * p.rise);
        const [cr, cg, cb] = p.col;
        const a = p.peak * fade;
        const g = ctx.createRadialGradient(x, y, 0, x, y, R);
        g.addColorStop(0, `rgba(${cr},${cg},${cb},${a})`);
        g.addColorStop(0.42, `rgba(${cr},${cg},${cb},${a * 0.40})`);
        g.addColorStop(0.72, `rgba(${cr},${cg},${cb},${a * 0.12})`);
        g.addColorStop(1, `rgba(${cr},${cg},${cb},0)`);
        ctx.fillStyle = g;
        ctx.fillRect(x - R, y - R, R * 2, R * 2);
      }

      // warm floor haze — grounds the frame, reads as light spill on smoke
      ctx.globalCompositeOperation = 'lighter';
      const drift = Math.sin(2 * Math.PI * t) * 0.06;
      const fl = ctx.createLinearGradient(0, BUF_H * 0.55, 0, BUF_H);
      fl.addColorStop(0, 'rgba(92,47,27,0)');
      fl.addColorStop(1, `rgba(104,54,31,${0.09 + drift * 0.25})`);
      ctx.fillStyle = fl;
      ctx.fillRect(0, BUF_H * 0.55, BUF_W, BUF_H * 0.45);

      // occluding volumes — drawn over the haze, subtractive in feel
      ctx.globalCompositeOperation = 'source-over';
      for (const o of occ) {
        const u = (t + o.phase) % 1;
        const fade = Math.sin(Math.PI * u);
        if (fade <= 0.002) continue;
        const R = o.r * S * (0.7 + u * 0.7);
        const x = (o.x + o.sway * Math.sin(2 * Math.PI * (o.swayK * (t + o.phase)))) * BUF_W;
        const y = BUF_H * (1.15 - u * o.rise);
        const a = o.peak * fade;
        const g = ctx.createRadialGradient(x, y, 0, x, y, R);
        g.addColorStop(0, `rgba(10,6,5,${a})`);
        g.addColorStop(0.5, `rgba(10,6,5,${a * 0.38})`);
        g.addColorStop(1, 'rgba(10,6,5,0)');
        ctx.fillStyle = g;
        ctx.fillRect(x - R, y - R, R * 2, R * 2);
      }
    }

    function drawFlash(tSec) {
      if (tSec >= flashAt) scheduleFlash(tSec);
      if (!flash) return;
      const lvl = flashLevel(flash, tSec);
      if (lvl <= 0.001) { if (tSec - flash.t0 > flash.dur) flash = null; return; }
      const a = flash.peak * lvl;
      const x = flash.x * BUF_W, y = flash.y * BUF_H, R = flash.r * Math.max(BUF_W, BUF_H);
      ctx.globalCompositeOperation = 'lighter';
      const g = ctx.createRadialGradient(x, y, 0, x, y, R);
      g.addColorStop(0, `rgba(214,226,224,${a})`);
      g.addColorStop(0.35, `rgba(160,190,188,${a * 0.42})`);
      g.addColorStop(1, 'rgba(120,150,150,0)');
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, BUF_W, BUF_H);

      // anamorphic streak — the cinema tell on any hard light source
      const sg = ctx.createLinearGradient(0, 0, BUF_W, 0);
      const sa = a * 0.85;
      sg.addColorStop(0, 'rgba(120,170,175,0)');
      sg.addColorStop(0.5, `rgba(196,220,222,${sa})`);
      sg.addColorStop(1, 'rgba(120,170,175,0)');
      ctx.fillStyle = sg;
      const sh = Math.max(1, BUF_H * 0.012 * (0.6 + lvl));
      ctx.fillRect(0, y - sh / 2, BUF_W, sh);
      ctx.globalCompositeOperation = 'source-over';
    }

    function frame(now) {
      if (!running) return;
      raf = requestAnimationFrame(frame);
      if (now - last < 1000 / FPS) return;
      last = now;
      draw(now / 1000);
      drawFlash(now / 1000);
    }

    const stop = () => { running = false; cancelAnimationFrame(raf); };
    const start = () => { if (!running) { running = true; last = -1e9; raf = requestAnimationFrame(frame); } };

    draw(0);
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    raf = requestAnimationFrame(frame);

    document.addEventListener('visibilitychange', () => (document.hidden ? stop() : start()));
    if ('IntersectionObserver' in window) {
      new IntersectionObserver((es) => {
        es.forEach((e) => (e.isIntersecting ? start() : stop()));
      }, { threshold: 0 }).observe(host);
    }
  }

  /* Auto-mount: every hero variant across the site gets the layer whether or
     not the markup declares it. Existing [data-hero-smoke] divs are reused. */
  const HOSTS = '[data-hero-smoke], .hero, .page-hero, .artist-page-hero';
  function boot() {
    document.querySelectorAll(HOSTS).forEach((el) => {
      let host = el.matches('[data-hero-smoke]') ? el : el.querySelector(':scope > [data-hero-smoke]');
      if (host && host.querySelector('.hero-smoke__cv')) return;
      const section = host ? host.parentElement : el;
      if (!section) return;
      section.classList.add('hero--smoke');
      if (!host) {
        host = document.createElement('div');
        host.className = 'hero-smoke';
        host.setAttribute('data-hero-smoke', '');
        host.setAttribute('aria-hidden', 'true');
        section.insertBefore(host, section.firstChild);
      }
      mount(host);
    });
  }

  /* Public hook for layers built after boot(). The player bar is created by
     player.js on DOMContentLoaded — after this file's own listener has already
     run — so it mounts its layer by calling this. Safe to call twice: a host
     that already has a canvas is skipped. */
  const mountLate = (host) => {
    if (!host || host.querySelector('.hero-smoke__cv')) return;
    mount(host);
  };
  window.BRSHeroSmoke = { mount: mountLate };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
